import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, RotateCw, X } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete, ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';

interface KitTemplate {
  id: string;
  code: string;
  name: string;
  specialty_id?: string;
  packaging_id?: string;
  validity_days?: number;
  active?: number;
  items?: KitItem[];
}
interface KitItem { model_id: string; model_name?: string; quantity: number; functional_check?: number }
interface ItemModel { id: string; sku: string; name: string }

export function KitTemplatesPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('ADMIN', 'SUPERVISOR');
  const qc = useQueryClient();

  const [editing, setEditing] = useState<KitTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<KitTemplate | null>(null);
  const [form, setForm] = useState<KitTemplate>({ id: '', code: '', name: '', items: [] });
  const [error, setError] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ['kit-templates'],
    queryFn: () => apiGet<{ items: KitTemplate[] }>('/v1/kit-templates?limit=200'),
  });
  const models = useQuery({
    queryKey: ['item-models', 'picker'],
    queryFn: () => apiGet<{ items: ItemModel[] }>('/v1/item-models?limit=500'),
  });
  const specialties = useQuery({
    queryKey: ['specialties', 'picker'],
    queryFn: () => apiGet<{ items: { id: string; name: string }[] }>('/v1/specialties?limit=200'),
  });
  const packaging = useQuery({
    queryKey: ['packaging', 'picker'],
    queryFn: () => apiGet<{ items: { id: string; name: string }[] }>('/v1/packaging?limit=200'),
  });

  const create = useMutation({
    mutationFn: (body: any) => apiPost('/v1/kit-templates', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kit-templates'] }); setCreating(false); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) => apiPatch(`/v1/kit-templates/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kit-templates'] }); setEditing(null); setError(null); },
    onError: (e) => setError(e instanceof ApiError ? `${e.code}: ${e.message}` : String(e)),
  });
  const del = useMutation({
    mutationFn: (id: string) => apiDelete(`/v1/kit-templates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['kit-templates'] }); setConfirming(null); },
  });

  function openCreate() {
    setForm({ id: '', code: '', name: '', items: [], validity_days: 180 });
    setError(null);
    setCreating(true);
  }
  function openEdit(t: KitTemplate) {
    setForm({ ...t, items: t.items ?? [] });
    setError(null);
    setEditing(t);
  }
  function addItem() {
    setForm((s) => ({ ...s, items: [...(s.items ?? []), { model_id: '', quantity: 1, functional_check: 0 }] }));
  }
  function updateItem(i: number, patch: Partial<KitItem>) {
    setForm((s) => ({
      ...s,
      items: (s.items ?? []).map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  }
  function removeItem(i: number) {
    setForm((s) => ({ ...s, items: (s.items ?? []).filter((_, idx) => idx !== i) }));
  }
  function submit() {
    const body: any = {
      code: form.code, name: form.name,
      specialty_id: form.specialty_id || undefined,
      packaging_id: form.packaging_id || undefined,
      validity_days: form.validity_days ? Number(form.validity_days) : undefined,
      items: (form.items ?? []).filter((i) => i.model_id).map((i) => ({
        model_id: i.model_id, quantity: Number(i.quantity) || 1, functional_check: i.functional_check ? 1 : 0,
      })),
    };
    if (editing) update.mutate({ id: editing.id, body });
    else create.mutate(body);
  }

  const rows = templates.data?.items ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Templates de Kit"
          subtitle="Receita reutilizável: instrumentos, quantidades, embalagem padrão e validade"
          action={
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" leadingIcon={<RotateCw className="h-4 w-4" />} onClick={() => templates.refetch()}>Atualizar</Button>
              {canWrite ? <Button size="sm" leadingIcon={<Plus className="h-4 w-4" />} onClick={openCreate}>Novo template</Button> : null}
            </div>
          }
        />
        <CardBody>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-700">
                  <th className="px-3 py-2 font-medium">Código</th>
                  <th className="px-3 py-2 font-medium">Nome</th>
                  <th className="px-3 py-2 font-medium">Itens</th>
                  <th className="px-3 py-2 text-right font-medium">Validade</th>
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {templates.isLoading ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-500">Carregando...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-neutral-500">Nenhum template.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 dark:border-neutral-700/50 dark:hover:bg-neutral-700/30">
                    <td className="px-3 py-2 font-mono text-xs">{r.code}</td>
                    <td className="px-3 py-2">{r.name}</td>
                    <td className="px-3 py-2"><Badge tone="info">{r.items?.length ?? 0}</Badge></td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.validity_days ?? '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.active === 0 ? <Badge tone="neutral">Inativo</Badge> : null}
                        {canWrite ? (
                          <button onClick={() => openEdit(r)} className="rounded p-1.5 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700" aria-label="Editar">
                            <Pencil className="h-4 w-4" />
                          </button>
                        ) : null}
                        {canWrite && r.active !== 0 ? (
                          <button onClick={() => setConfirming(r)} className="rounded p-1.5 text-danger hover:bg-danger-bg" aria-label="Desativar">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={creating || !!editing}
        title={editing ? `Editar ${editing.name}` : 'Novo template de kit'}
        size="lg"
        onClose={() => { setCreating(false); setEditing(null); setError(null); }}
        footer={
          <>
            <Button variant="ghost" onClick={() => { setCreating(false); setEditing(null); }}>Cancelar</Button>
            <Button onClick={submit} loading={create.isPending || update.isPending}>{editing ? 'Salvar' : 'Criar'}</Button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Código *" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Input label="Nome *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Especialidade</label>
            <select
              value={form.specialty_id ?? ''}
              onChange={(e) => setForm({ ...form, specialty_id: e.target.value })}
              className="h-10 rounded border border-neutral-300 bg-neutral-0 px-3 text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
            >
              <option value="">— —</option>
              {specialties.data?.items.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Embalagem padrão</label>
            <select
              value={form.packaging_id ?? ''}
              onChange={(e) => setForm({ ...form, packaging_id: e.target.value })}
              className="h-10 rounded border border-neutral-300 bg-neutral-0 px-3 text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
            >
              <option value="">— —</option>
              {packaging.data?.items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <Input
            label="Validade (dias)"
            type="number"
            value={form.validity_days ?? ''}
            onChange={(e) => setForm({ ...form, validity_days: Number(e.target.value) })}
            hint="sobrescreve validade da embalagem"
          />
        </div>

        <div className="mt-6">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">Composição</h4>
            <Button variant="secondary" size="sm" leadingIcon={<Plus className="h-4 w-4" />} onClick={addItem}>Adicionar item</Button>
          </div>
          {(form.items ?? []).length === 0 ? (
            <p className="rounded border border-dashed border-neutral-300 p-4 text-center text-xs text-neutral-500 dark:border-neutral-700">
              Nenhum item. Adicione modelos de instrumento que compõem este kit.
            </p>
          ) : (
            <div className="space-y-2">
              {(form.items ?? []).map((it, i) => (
                <div key={i} className="grid grid-cols-12 items-end gap-2 rounded border border-neutral-200 p-2 dark:border-neutral-700">
                  <div className="col-span-7 flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Modelo</label>
                    <select
                      value={it.model_id}
                      onChange={(e) => updateItem(i, { model_id: e.target.value })}
                      className="h-10 rounded border border-neutral-300 bg-neutral-0 px-3 text-sm outline-none focus:border-brand-500 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
                    >
                      <option value="">— Selecione —</option>
                      {models.data?.items.map((m) => <option key={m.id} value={m.id}>{m.sku} · {m.name}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      label="Qtd"
                      type="number"
                      min={1}
                      value={it.quantity}
                      onChange={(e) => updateItem(i, { quantity: Number(e.target.value) })}
                    />
                  </div>
                  <div className="col-span-2 flex h-10 items-center">
                    <label className="flex items-center gap-1.5 text-xs text-neutral-700 dark:text-neutral-300">
                      <input
                        type="checkbox"
                        checked={!!it.functional_check}
                        onChange={(e) => updateItem(i, { functional_check: e.target.checked ? 1 : 0 })}
                        className="h-4 w-4 rounded border-neutral-300 text-brand-500 focus:ring-brand-500"
                      />
                      Func. check
                    </label>
                  </div>
                  <div className="col-span-1 flex h-10 items-center justify-end">
                    <button onClick={() => removeItem(i)} className="rounded p-1.5 text-danger hover:bg-danger-bg" aria-label="Remover">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
      </Modal>

      <Modal
        open={!!confirming}
        title="Desativar template"
        size="sm"
        onClose={() => setConfirming(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)}>Cancelar</Button>
            <Button variant="danger" onClick={() => confirming && del.mutate(confirming.id)} loading={del.isPending}>Desativar</Button>
          </>
        }
      >
        <p className="text-sm">Desativar template <strong>{confirming?.name}</strong>? Kits já montados não serão afetados.</p>
      </Modal>
    </div>
  );
}
